import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'
import { Textarea } from '@quebon/ui/components/textarea'
import { handleApiError } from '@/api/client'
import {
  changeLeadPriority,
  changeLeadStage,
  correctContract,
  createContractTransition,
  decideQualification,
  fetchContractTransition,
  fetchLeadDetail,
  fetchLeadHistory,
  holdCloseLead,
  isStaleLeadError,
  updateLead,
  type LeadDetail,
} from '@/api/lcCrm'
import {
  ATTENDANCE_STATUS_LABEL,
  AUDIT_ACTION_LABEL,
  BUDGET_BAND_LABEL,
  HOLD_CLOSE_REASON_LABEL,
  LEAD_PRIORITY_LABEL,
  LEAD_PRIORITY_ORDER,
  LEAD_STAGE_LABEL,
  LEAD_STAGE_ORDER,
  QUALIFICATION_RESULT_LABEL,
  SPACE_STATUS_LABEL,
  formatKrw,
  formatKstDateTime,
  formatLocalDate,
  labelOf,
  formatPhone,
} from './crmLabels'
import LeadDocumentPanel from './LeadDocumentPanel'
import { normalizeOpenYear, validateQualificationForm } from './leadFormRules'
import {
  DefinitionRow,
  ErrorBlock,
  LabeledSelect,
  LoadingBlock,
  PriorityBadge,
  StageBadge,
  StaleDaysCell,
} from './crmComponents'
import { LeadActivityTab } from './LeadActivityTab'
import { ContactNotesPanel } from './ContactNotesPanel'

/**
 * 리드 상세 (설계 §12).
 *
 * 모든 변경은 상세가 읽어 둔 `version`을 `expectedVersion`으로 싣는다. 서버가
 * `CRM_LEAD_STALE`로 거절하면 다시 읽는다 — 낡은 버전으로 재시도하면 같은 곳에서 또 막힌다.
 */
export function CrmLeadDetailPage() {
  const params = useParams<{ leadId: string }>()
  const leadId = Number(params.leadId)
  const queryClient = useQueryClient()

  const { data: lead, isLoading, error } = useQuery({
    queryKey: ['lc-crm-lead', leadId],
    queryFn: () => fetchLeadDetail(leadId),
    enabled: Number.isFinite(leadId),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lc-crm-lead', leadId] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-lead-history', leadId] })
    queryClient.invalidateQueries({ queryKey: ['lc-crm-leads'] })
  }

  if (!Number.isFinite(leadId)) return <ErrorBlock message="리드 ID가 올바르지 않습니다." />
  if (isLoading) return <LoadingBlock />
  if (error || !lead) return <ErrorBlock message="리드를 불러오지 못했습니다." />

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon-sm" aria-label="리드 목록으로" render={<Link to="/lc-crm/leads" />}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              {lead.contactName ?? '이름 없음'}
              <span className="text-sm font-normal text-muted-foreground tabular-nums">
                리드 #{lead.leadId}
              </span>
            </h2>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="tabular-nums">{formatPhone(lead.phoneNormalized)}</span>
              <StageBadge stage={lead.stage} />
              <PriorityBadge priority={lead.priority} />
            </p>
          </div>
        </div>
        <LeadActionBar lead={lead} onDone={invalidate} />
      </div>

      {lead.stage === 'HOLD_CLOSED' && (
        <Alert variant="destructive">
          <AlertTitle>보류 · 종결된 리드입니다</AlertTitle>
          <AlertDescription>
            사유 {labelOf(HOLD_CLOSE_REASON_LABEL, lead.holdCloseReasonCode)}
            {lead.holdCloseReasonNote ? ` — ${lead.holdCloseReasonNote}` : ''} (
            {formatKstDateTime(lead.closedAt)})
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="basic">
        <TabsList variant="line" aria-label="리드 상세">
          <TabsTrigger value="basic">기본</TabsTrigger>
          <TabsTrigger value="qualification">창업상황 · 적격성</TabsTrigger>
          <TabsTrigger value="activity">상담 · 첨부 · 할 일</TabsTrigger>
          <TabsTrigger value="contract">제안 · 계약</TabsTrigger>
          <TabsTrigger value="history">이력</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="pt-4">
          <BasicTab lead={lead} onDone={invalidate} />
          <div className="mt-6">
            <ContactNotesPanel key={lead.contactId} contactId={lead.contactId} />
          </div>
        </TabsContent>
        <TabsContent value="qualification" className="pt-4">
          <QualificationTab lead={lead} onDone={invalidate} />
        </TabsContent>
        <TabsContent value="activity" className="pt-4">
          <LeadActivityTab leadId={lead.leadId} />
        </TabsContent>
        <TabsContent value="contract" className="pt-4">
          <ContractTab lead={lead} onDone={invalidate} />
        </TabsContent>
        <TabsContent value="history" className="pt-4">
          <HistoryTab leadId={lead.leadId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------- 상단 행동

function LeadActionBar({ lead, onDone }: { lead: LeadDetail; onDone: () => void }) {
  const [stageOpen, setStageOpen] = useState(false)
  const [holdOpen, setHoldOpen] = useState(false)

  return (
    <div className="flex items-center gap-2">
      <PriorityControl lead={lead} onDone={onDone} />
      <Button variant="outline" onClick={() => setStageOpen(true)}>단계 변경</Button>
      <Button variant="destructive" onClick={() => setHoldOpen(true)}>보류 · 종결</Button>

      {stageOpen && <StageDialog lead={lead} onClose={() => setStageOpen(false)} onDone={onDone} />}
      {holdOpen && <HoldCloseDialog lead={lead} onClose={() => setHoldOpen(false)} onDone={onDone} />}
    </div>
  )
}

/** 낙관적 잠금 실패를 한 곳에서 다룬다. 화면마다 다르게 안내하면 운영자가 무엇을 해야 할지 모른다. */
function useLeadMutation<TArgs>(
  fn: (args: TArgs) => Promise<unknown>,
  { onDone, successMessage }: { onDone: () => void; successMessage: string },
) {
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(successMessage)
      onDone()
    },
    onError: (err) => {
      if (isStaleLeadError(err)) {
        toast.error('다른 곳에서 리드가 수정되어 최신 내용을 다시 불러왔습니다. 입력값은 유지되니 확인 후 다시 저장해 주세요.')
        onDone()
        return
      }
      toast.error(handleApiError(err) || '변경에 실패했습니다')
    },
  })
}

function PriorityControl({ lead, onDone }: { lead: LeadDetail; onDone: () => void }) {
  const mutation = useLeadMutation(
    (priority: string) =>
      changeLeadPriority(lead.leadId, { priority, expectedVersion: lead.version }),
    { onDone, successMessage: '우선순위를 바꿨습니다' },
  )

  return (
    <LabeledSelect
      className="w-36"
      ariaLabel="우선순위"
      value={lead.priority}
      onChange={(next) => {
        if (next !== lead.priority) mutation.mutate(next)
      }}
      items={LEAD_PRIORITY_ORDER.map((value) => ({ value, label: LEAD_PRIORITY_LABEL[value] }))}
    />
  )
}

function StageDialog({
  lead,
  initialStage,
  onClose,
  onDone,
}: {
  lead: LeadDetail
  initialStage?: string
  onClose: () => void
  onDone: () => void
}) {
  const [toStage, setToStage] = useState(
    initialStage ?? (lead.stage === 'HOLD_CLOSED' ? 'CONSULTING' : lead.stage),
  )
  const [reasonCode, setReasonCode] = useState('')
  const [reasonNote, setReasonNote] = useState('')

  const mutation = useLeadMutation(
    () =>
      changeLeadStage(lead.leadId, {
        toStage,
        reasonCode: reasonCode || undefined,
        reasonNote: reasonNote || undefined,
        expectedVersion: lead.version,
      }),
    {
      onDone: () => {
        onDone()
        onClose()
      },
      successMessage: '단계를 바꿨습니다',
    },
  )

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>단계 변경</DialogTitle>
          <DialogDescription>
            현재 {labelOf(LEAD_STAGE_LABEL, lead.stage)} 단계입니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel>옮길 단계</FieldLabel>
            <LabeledSelect
              ariaLabel="옮길 단계"
              value={toStage}
              onChange={setToStage}
              items={LEAD_STAGE_ORDER.map((stage) => ({ value: stage, label: LEAD_STAGE_LABEL[stage] }))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="stage-reason-code">사유 코드 (선택)</FieldLabel>
            <Input id="stage-reason-code" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="stage-reason-note">메모 (선택)</FieldLabel>
            <Textarea id="stage-reason-note" rows={2} value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button disabled={mutation.isPending || toStage === lead.stage} onClick={() => mutation.mutate(undefined as never)}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            변경
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** 설계 §19.1. 사유 코드는 필수고 OTHER는 메모까지 필수다. */
function HoldCloseDialog({
  lead,
  onClose,
  onDone,
}: {
  lead: LeadDetail
  onClose: () => void
  onDone: () => void
}) {
  const [reasonCode, setReasonCode] = useState('NO_RESPONSE')
  const [reasonNote, setReasonNote] = useState('')
  const noteRequired = reasonCode === 'OTHER'

  const mutation = useLeadMutation(
    () =>
      holdCloseLead(lead.leadId, {
        reasonCode,
        reasonNote: reasonNote || undefined,
        expectedVersion: lead.version,
      }),
    {
      onDone: () => {
        onDone()
        onClose()
      },
      successMessage: '리드를 보류 · 종결했습니다',
    },
  )

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>보류 · 종결</DialogTitle>
          <DialogDescription>
            진행 축에서 빠집니다. 사유는 이탈 분포 집계에 그대로 쓰입니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel>사유</FieldLabel>
            <LabeledSelect
              ariaLabel="사유"
              value={reasonCode}
              onChange={setReasonCode}
              items={Object.entries(HOLD_CLOSE_REASON_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Field>
          <Field data-invalid={noteRequired && !reasonNote.trim() ? true : undefined}>
            <FieldLabel htmlFor="hold-note">
              메모{noteRequired ? ' (기타 사유는 필수)' : ' (선택)'}
            </FieldLabel>
            <Textarea
              id="hold-note"
              rows={3}
              aria-invalid={noteRequired && !reasonNote.trim()}
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
            />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button
            variant="destructive"
            disabled={mutation.isPending || (noteRequired && !reasonNote.trim())}
            onClick={() => mutation.mutate(undefined as never)}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            보류 · 종결
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------- 기본 탭

function BasicTab({ lead, onDone }: { lead: LeadDetail; onDone: () => void }) {
  const [team, setTeam] = useState(lead.assignedTeam ?? '')
  const [attendance, setAttendance] = useState(lead.attendanceStatus)

  const mutation = useLeadMutation(
    () =>
      updateLead(lead.leadId, {
        assignedTeam: team || null,
        attendanceStatus: attendance,
        expectedVersion: lead.version,
      }),
    { onDone, successMessage: '리드 정보를 저장했습니다' },
  )

  const dirty = team !== (lead.assignedTeam ?? '') || attendance !== lead.attendanceStatus

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>연락처</CardTitle>
        </CardHeader>
        <CardContent>
          <dl>
            <DefinitionRow label="이름">{lead.contactName ?? '-'}</DefinitionRow>
            <DefinitionRow label="연락처">
              <span className="tabular-nums">{formatPhone(lead.phoneNormalized)}</span>
            </DefinitionRow>
            <DefinitionRow label="연락처 ID">
              <span className="tabular-nums">{lead.contactId}</span>
            </DefinitionRow>
            <DefinitionRow label="재신청 횟수">
              <span className="tabular-nums">{lead.reapplicationCount}회</span>
            </DefinitionRow>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>진행 상태</CardTitle>
        </CardHeader>
        <CardContent>
          <dl>
            <DefinitionRow label="단계"><StageBadge stage={lead.stage} /></DefinitionRow>
            <DefinitionRow label="단계 진입">{formatKstDateTime(lead.stageEnteredAt)}</DefinitionRow>
            <DefinitionRow label="최근 활동">{formatKstDateTime(lead.lastActivityAt)}</DefinitionRow>
            <DefinitionRow label="체류일">
              <StaleDaysCell
                lastActivityAt={lead.lastActivityAt}
                stageEnteredAt={lead.stageEnteredAt}
              />
            </DefinitionRow>
            <DefinitionRow label="리드 전환">{formatKstDateTime(lead.convertedAt)}</DefinitionRow>
          </dl>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>담당 · 참석</CardTitle>
          <CardDescription>담당자와 설명회 참석 상태를 기록합니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <Field className="w-56">
            <FieldLabel htmlFor="basic-team">담당자</FieldLabel>
            <Input id="basic-team" maxLength={50} value={team} onChange={(e) => setTeam(e.target.value)} />
          </Field>
          <Field className="w-44">
            <FieldLabel>설명회 참석</FieldLabel>
            <LabeledSelect
              ariaLabel="설명회 참석"
              value={attendance}
              onChange={setAttendance}
              items={Object.entries(ATTENDANCE_STATUS_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Field>
          <Button className="mb-0.5" disabled={!dirty || mutation.isPending} onClick={() => mutation.mutate(undefined as never)}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            저장
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------- 창업상황 · 적격성 탭

/**
 * FR-25. 적격성은 자동 채점이 아니라 운영자 판정이다 (설계 §19.7).
 *
 * `space_status`·`budget_band`·`family_consent`가 비면 서버가 `CRM_QUALIFICATION_INCOMPLETE`로
 * 거절한다. 그래서 판정 버튼을 누르기 전에 무엇이 비었는지 화면에서 먼저 말해 준다.
 */
function QualificationTab({ lead, onDone }: { lead: LeadDetail; onDone: () => void }) {
  const initialForm = {
    occupation: lead.occupation ?? '',
    educationExperience: lead.educationExperience ?? '',
    educationYears: lead.educationYears?.toString() ?? '',
    desiredOpenYear: lead.desiredOpenYear?.toString() ?? '',
    desiredOpenMonth: lead.desiredOpenMonth?.toString() ?? '',
    desiredSido: lead.desiredSido ?? '',
    desiredSigungu: lead.desiredSigungu ?? '',
    spaceStatus: lead.spaceStatus ?? '',
    budgetBand: lead.budgetBand ?? '',
    familyConsent: lead.familyConsent == null ? '' : String(lead.familyConsent),
  }
  const [form, setForm] = useState(initialForm)
  const [decideOpen, setDecideOpen] = useState(false)

  const save = useLeadMutation(
    (form: typeof initialForm) =>
      updateLead(lead.leadId, {
        occupation: form.occupation || null,
        educationExperience: form.educationExperience || null,
        educationYears: form.educationYears ? Number(form.educationYears) : null,
        desiredOpenYear: form.desiredOpenYear ? Number(form.desiredOpenYear) : null,
        desiredOpenMonth: form.desiredOpenMonth ? Number(form.desiredOpenMonth) : null,
        desiredSido: form.desiredSido || null,
        desiredSigungu: form.desiredSigungu || null,
        spaceStatus: form.spaceStatus || null,
        budgetBand: form.budgetBand || null,
        familyConsent: form.familyConsent === '' ? null : form.familyConsent === 'true',
        // 서버는 null을 "건드리지 않음"으로 해석하므로, 비운(미확인) 필드는 명시적 초기화 목록으로 보낸다.
        clearFields: (
          [
            ['occupation', form.occupation],
            ['educationExperience', form.educationExperience],
            ['educationYears', form.educationYears],
            ['desiredOpenYear', form.desiredOpenYear],
            ['desiredOpenMonth', form.desiredOpenMonth],
            ['desiredSido', form.desiredSido],
            ['desiredSigungu', form.desiredSigungu],
            ['spaceStatus', form.spaceStatus],
            ['budgetBand', form.budgetBand],
            ['familyConsent', form.familyConsent],
          ] as const
        )
          .filter(([, value]) => value === '')
          .map(([name]) => name),
        expectedVersion: lead.version,
      }),
    { onDone, successMessage: '창업 상황을 저장했습니다' },
  )

  const missing = [
    !lead.spaceStatus && '공간 상태',
    !lead.budgetBand && '예산 구간',
    lead.familyConsent == null && '가족 동의',
  ].filter(Boolean) as string[]

  // 서버는 한 필드만 어긋나도 요청 전체를 거절해 나머지 값까지 저장되지 않는다. 먼저 보정하고 거른다.
  const handleSave = () => {
    const normalized = { ...form, desiredOpenYear: normalizeOpenYear(form.desiredOpenYear) }
    const errors = validateQualificationForm(normalized)
    if (errors.length > 0) {
      toast.error(errors.join('\n'))
      return
    }
    setForm(normalized)
    save.mutate(normalized)
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>창업 상황</CardTitle>
          <CardDescription>적격성 판정의 근거가 되는 값입니다.</CardDescription>
        </CardHeader>
        <CardContent>
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="q-occupation">직업</FieldLabel>
                <Input id="q-occupation" maxLength={100} value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-edu">교육 경력</FieldLabel>
                <Input id="q-edu" maxLength={100} value={form.educationExperience} onChange={(e) => setForm({ ...form, educationExperience: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-edu-years">교육 경력 연수</FieldLabel>
                <Input id="q-edu-years" type="number" min={0} value={form.educationYears} onChange={(e) => setForm({ ...form, educationYears: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel>가족 동의</FieldLabel>
                <LabeledSelect
                  ariaLabel="가족 동의"
                  value={form.familyConsent || 'unknown'}
                  onChange={(next) => setForm({ ...form, familyConsent: next === 'unknown' ? '' : next })}
                  items={[
                    { value: 'unknown', label: '미확인' },
                    { value: 'true', label: '동의' },
                    { value: 'false', label: '미동의' },
                  ]}
                />
              </Field>
              <Field>
                <FieldLabel>공간 상태</FieldLabel>
                <LabeledSelect
                  ariaLabel="공간 상태"
                  value={form.spaceStatus || 'unknown'}
                  onChange={(next) => setForm({ ...form, spaceStatus: next === 'unknown' ? '' : next })}
                  items={[
                    { value: 'unknown', label: '미확인' },
                    ...Object.entries(SPACE_STATUS_LABEL).map(([value, label]) => ({ value, label })),
                  ]}
                />
              </Field>
              <Field>
                <FieldLabel>예산 구간</FieldLabel>
                <LabeledSelect
                  ariaLabel="예산 구간"
                  value={form.budgetBand || 'unknown'}
                  onChange={(next) => setForm({ ...form, budgetBand: next === 'unknown' ? '' : next })}
                  items={[
                    { value: 'unknown', label: '미확인' },
                    ...Object.entries(BUDGET_BAND_LABEL).map(([value, label]) => ({ value, label })),
                  ]}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-year">희망 개점 연도</FieldLabel>
                <Input
                  id="q-year"
                  type="number"
                  min={2000}
                  max={2100}
                  placeholder="예: 2026"
                  value={form.desiredOpenYear}
                  onChange={(e) => setForm({ ...form, desiredOpenYear: e.target.value })}
                  onBlur={(e) => setForm({ ...form, desiredOpenYear: normalizeOpenYear(e.target.value) })}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-month">희망 개점 월</FieldLabel>
                <Input id="q-month" type="number" min={1} max={12} value={form.desiredOpenMonth} onChange={(e) => setForm({ ...form, desiredOpenMonth: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-sido">희망 시·도</FieldLabel>
                <Input id="q-sido" maxLength={20} value={form.desiredSido} onChange={(e) => setForm({ ...form, desiredSido: e.target.value })} />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-sigungu">희망 시·군·구</FieldLabel>
                <Input id="q-sigungu" maxLength={40} value={form.desiredSigungu} onChange={(e) => setForm({ ...form, desiredSigungu: e.target.value })} />
              </Field>
            </div>
            <Button className="self-start" disabled={save.isPending} onClick={handleSave}>
              {save.isPending && <Loader2 className="size-4 animate-spin" />}
              저장
            </Button>
          </FieldGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>적격성 판정</CardTitle>
          <CardDescription>
            시스템이 채점하지 않습니다. 운영자가 판정하고 그 결과가 계약 축 진행을 막거나 엽니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl>
            <DefinitionRow label="판정">
              {lead.qualificationResult ? (
                <Badge variant={lead.qualificationResult === 'NOT_QUALIFIED' ? 'destructive' : 'default'}>
                  {labelOf(QUALIFICATION_RESULT_LABEL, lead.qualificationResult)}
                </Badge>
              ) : (
                <span className="text-muted-foreground">미판정</span>
              )}
            </DefinitionRow>
            <DefinitionRow label="판정 메모">
              <span className="whitespace-pre-wrap break-words">{lead.qualificationNote ?? '-'}</span>
            </DefinitionRow>
            <DefinitionRow label="판정 시각">{formatKstDateTime(lead.qualificationDecidedAt)}</DefinitionRow>
            <DefinitionRow label="판정자">
              {lead.qualificationDecidedByName ?? (lead.qualificationDecidedBy ? `#${lead.qualificationDecidedBy}` : '-')}
            </DefinitionRow>
          </dl>

          <LeadDocumentPanel
            leadId={lead.leadId}
            documentType="QUALIFICATION"
            description="판정 근거가 된 자료를 여기에 보관합니다 (이미지·PDF·doc·hwp, 20MB)."
          />

          {missing.length > 0 && (
            <Alert>
              <AlertTitle>판정 전에 채워야 할 값이 있습니다</AlertTitle>
              <AlertDescription>{missing.join(', ')}을(를) 저장한 뒤 판정할 수 있습니다.</AlertDescription>
            </Alert>
          )}

          <Button className="self-start" disabled={missing.length > 0} onClick={() => setDecideOpen(true)}>
            적격성 판정
          </Button>
        </CardContent>
      </Card>

      {decideOpen && (
        <QualificationDialog lead={lead} onClose={() => setDecideOpen(false)} onDone={onDone} />
      )}
    </div>
  )
}

function QualificationDialog({
  lead,
  onClose,
  onDone,
}: {
  lead: LeadDetail
  onClose: () => void
  onDone: () => void
}) {
  const [result, setResult] = useState(lead.qualificationResult ?? 'QUALIFIED')
  const [note, setNote] = useState(lead.qualificationNote ?? '')
  const noteRequired = result !== 'QUALIFIED'

  const mutation = useLeadMutation(
    () =>
      decideQualification(lead.leadId, {
        result,
        note: note || undefined,
        expectedVersion: lead.version,
      }),
    {
      onDone: () => {
        onDone()
        onClose()
      },
      successMessage: '적격성을 판정했습니다',
    },
  )

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>적격성 판정</DialogTitle>
          <DialogDescription>
            부적격은 계약 축 전진을 막습니다. 후진과 보류 · 종결은 계속 가능합니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel>판정</FieldLabel>
            <LabeledSelect
              ariaLabel="판정"
              value={result}
              onChange={setResult}
              items={Object.entries(QUALIFICATION_RESULT_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Field>
          <Field data-invalid={noteRequired && !note.trim() ? true : undefined}>
            <FieldLabel htmlFor="qual-note">
              근거 메모{noteRequired ? ' (적격이 아니면 필수)' : ' (선택)'}
            </FieldLabel>
            <Textarea
              id="qual-note"
              rows={3}
              aria-invalid={noteRequired && !note.trim()}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button
            disabled={mutation.isPending || (noteRequired && !note.trim())}
            onClick={() => mutation.mutate(undefined as never)}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            판정
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------- 제안 · 계약 탭

/**
 * Phase 1은 날짜 필드만 쓴다 (설계 §19.4). 제안 상태 enum이나 버전 컬럼은 없다.
 *
 * 계약 전환은 `LEAD_CONTRACTED` 이벤트를 PENDING으로 하나 만드는 데까지다. 가맹점 organization
 * 생성은 Phase 3이 소비한다.
 */
function ContractTab({ lead, onDone }: { lead: LeadDetail; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [correctOpen, setCorrectOpen] = useState(false)
  const [stageOpen, setStageOpen] = useState(false)
  // 서버 규칙과 동일: 계약 제안 단계, 또는 이벤트 없이 CONTRACT에 먼저 도달한 리드의 구제.
  const contractStageAllowed = isContractStageAllowed(lead.stage)
  const [disclosureOn, setDisclosureOn] = useState(lead.disclosureProvidedOn ?? '')
  const [proposedOn, setProposedOn] = useState(lead.contractProposedOn ?? '')

  const { data: transition } = useQuery({
    queryKey: ['lc-crm-contract', lead.leadId],
    queryFn: () => fetchContractTransition(lead.leadId),
    retry: false,
  })

  const scheduleDirty =
    disclosureOn !== (lead.disclosureProvidedOn ?? '') || proposedOn !== (lead.contractProposedOn ?? '')
  const saveSchedule = useLeadMutation(
    () =>
      updateLead(lead.leadId, {
        disclosureProvidedOn: disclosureOn || null,
        contractProposedOn: proposedOn || null,
        clearFields: [
          ...(disclosureOn === '' ? ['disclosureProvidedOn'] : []),
          ...(proposedOn === '' ? ['contractProposedOn'] : []),
        ],
        expectedVersion: lead.version,
      }),
    { onDone, successMessage: '제안 · 계약 일정을 저장했습니다' },
  )

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>제안 · 계약 일정</CardTitle>
          <CardDescription>정보공개서 제공과 계약 제안은 여기서 기록합니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="ct-disclosure">정보공개서 제공일</FieldLabel>
              <Input id="ct-disclosure" type="date" value={disclosureOn} onChange={(e) => setDisclosureOn(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ct-proposed">계약 제안일</FieldLabel>
              <Input id="ct-proposed" type="date" value={proposedOn} onChange={(e) => setProposedOn(e.target.value)} />
            </Field>
          </div>
          <Button
            className="self-start"
            disabled={!scheduleDirty || saveSchedule.isPending}
            onClick={() => saveSchedule.mutate(undefined as never)}
          >
            {saveSchedule.isPending && <Loader2 className="size-4 animate-spin" />}
            일정 저장
          </Button>
          <div className="grid gap-3 sm:grid-cols-2">
            <LeadDocumentPanel
              leadId={lead.leadId}
              documentType="DISCLOSURE"
              description="예비 가맹점주에게 제공한 정보공개서 원본"
            />
            <LeadDocumentPanel
              leadId={lead.leadId}
              documentType="CONTRACT"
              description="체결한 계약서 원본 (스캔본 포함)"
            />
          </div>
          <dl>
            <DefinitionRow label="계약 체결일">{formatLocalDate(lead.contractSignedOn)}</DefinitionRow>
            <DefinitionRow label="계약 금액">{formatKrw(lead.contractAmount)}</DefinitionRow>
            <DefinitionRow label="개점 예정일">{formatLocalDate(lead.expectedOpenOn)}</DefinitionRow>
            <DefinitionRow label="확정 지역">
              {[lead.confirmedSido, lead.confirmedSigungu].filter(Boolean).join(' ') || '-'}
            </DefinitionRow>
          </dl>
          {transition && (
            <Button variant="outline" className="self-start" onClick={() => setCorrectOpen(true)}>
              계약 정보 정정
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>계약 전환 이벤트</CardTitle>
          <CardDescription>
            Phase 1은 이벤트 생성까지 책임집니다. 가맹점 생성은 후속 단계가 소비합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {transition ? (
            <dl>
              <DefinitionRow label="이벤트">{transition.eventType}</DefinitionRow>
              <DefinitionRow label="상태"><Badge variant="secondary">{transition.status}</Badge></DefinitionRow>
              <DefinitionRow label="schema version">
                <span className="tabular-nums">v{transition.payloadSchemaVersion}</span>
              </DefinitionRow>
              <DefinitionRow label="발생 시각">{formatKstDateTime(transition.occurredAt)}</DefinitionRow>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">아직 계약 전환 이벤트가 없습니다.</p>
          )}
          {!transition && (
            <ContractStageNotice
              stage={lead.stage}
              onRequestStageChange={() => setStageOpen(true)}
            />
          )}
          <Button className="self-start" disabled={Boolean(transition) || !contractStageAllowed} onClick={() => setOpen(true)}>
            계약 전환
          </Button>
        </CardContent>
      </Card>

      {open && (
        <ContractDialog lead={lead} onClose={() => setOpen(false)} onDone={onDone} />
      )}
      {correctOpen && (
        <ContractCorrectionDialog lead={lead} onClose={() => setCorrectOpen(false)} onDone={onDone} />
      )}
      {stageOpen && (
        <StageDialog
          lead={lead}
          initialStage="CONTRACT_PROPOSAL"
          onClose={() => setStageOpen(false)}
          onDone={onDone}
        />
      )}
    </div>
  )
}

function isContractStageAllowed(stage: string): boolean {
  return stage === 'CONTRACT_PROPOSAL' || stage === 'CONTRACT'
}

export function ContractStageNotice({
  stage,
  onRequestStageChange,
}: {
  stage: string
  onRequestStageChange: () => void
}) {
  if (isContractStageAllowed(stage)) return null

  return (
    <Alert>
      <AlertTitle>계약 제안 단계에서 실행할 수 있습니다</AlertTitle>
      <AlertDescription>
        현재 단계는 {labelOf(LEAD_STAGE_LABEL, stage)}입니다. 계약 제안 단계로 변경한 뒤 계약
        정보를 입력해 주세요.
      </AlertDescription>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3"
        onClick={onRequestStageChange}
      >
        계약 제안 단계로 변경
      </Button>
    </Alert>
  )
}

/** 계약 정보 정정 — 리드 필드만 고친다. 전환 이벤트 payload는 최초값 그대로 남는다. */
function ContractCorrectionDialog({
  lead,
  onClose,
  onDone,
}: {
  lead: LeadDetail
  onClose: () => void
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const [signedOn, setSignedOn] = useState(lead.contractSignedOn ?? '')
  const [amount, setAmount] = useState(lead.contractAmount?.toString() ?? '')
  const [sido, setSido] = useState(lead.confirmedSido ?? '')
  const [sigungu, setSigungu] = useState(lead.confirmedSigungu ?? '')
  const [openOn, setOpenOn] = useState(lead.expectedOpenOn ?? '')

  const mutation = useMutation({
    mutationFn: () =>
      correctContract(lead.leadId, {
        contractSignedOn: signedOn || undefined,
        contractAmount: amount ? Number(amount) : undefined,
        confirmedSido: sido || undefined,
        confirmedSigungu: sigungu || undefined,
        expectedOpenOn: openOn || undefined,
        clearFields: [
          ...(sido === '' && lead.confirmedSido ? ['confirmedSido'] : []),
          ...(sigungu === '' && lead.confirmedSigungu ? ['confirmedSigungu'] : []),
          ...(openOn === '' && lead.expectedOpenOn ? ['expectedOpenOn'] : []),
        ],
        expectedVersion: lead.version,
      }),
    onSuccess: () => {
      toast.success('계약 정보를 정정했습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-contract', lead.leadId] })
      onDone()
      onClose()
    },
    onError: (err) => {
      if (isStaleLeadError(err)) {
        toast.error('다른 곳에서 리드가 수정되어 최신 내용을 다시 불러왔습니다. 확인 후 다시 정정해 주세요.')
        onDone()
        return
      }
      toast.error(handleApiError(err) || '계약 정보 정정 실패')
    },
  })

  const ready = Boolean(signedOn && amount && Number(amount) > 0)

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>계약 정보 정정</DialogTitle>
          <DialogDescription>
            리드의 계약 필드만 바뀝니다. 계약 전환 이벤트는 최초값 그대로 남고, 정정 내역은 감사 로그에 기록됩니다.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="cc-signed">계약 체결일</FieldLabel>
            <Input id="cc-signed" type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="cc-amount">계약 금액 (원)</FieldLabel>
            <Input id="cc-amount" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="cc-sido">확정 시·도</FieldLabel>
              <Input id="cc-sido" maxLength={20} value={sido} onChange={(e) => setSido(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="cc-sigungu">확정 시·군·구</FieldLabel>
              <Input id="cc-sigungu" maxLength={40} value={sigungu} onChange={(e) => setSigungu(e.target.value)} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="cc-open">개점 예정일</FieldLabel>
            <Input id="cc-open" type="date" value={openOn} onChange={(e) => setOpenOn(e.target.value)} />
          </Field>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button disabled={!ready || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            정정 저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ContractDialog({
  lead,
  onClose,
  onDone,
}: {
  lead: LeadDetail
  onClose: () => void
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const [signedOn, setSignedOn] = useState(lead.contractSignedOn ?? '')
  const [amount, setAmount] = useState(lead.contractAmount?.toString() ?? '')
  const [sido, setSido] = useState(lead.confirmedSido ?? '')
  const [sigungu, setSigungu] = useState(lead.confirmedSigungu ?? '')
  const [openOn, setOpenOn] = useState(lead.expectedOpenOn ?? '')
  const [confirmed, setConfirmed] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      createContractTransition({
        leadId: lead.leadId,
        contractSignedOn: signedOn,
        contractAmount: Number(amount),
        confirmedSido: sido || undefined,
        confirmedSigungu: sigungu || undefined,
        expectedOpenOn: openOn || undefined,
      }),
    onSuccess: () => {
      toast.success('계약 전환 이벤트를 만들었습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-contract', lead.leadId] })
      onDone()
      onClose()
    },
    onError: (err) => toast.error(handleApiError(err) || '계약 전환 실패'),
  })

  const ready = Boolean(signedOn && amount && Number(amount) > 0)

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>계약 전환</DialogTitle>
          <DialogDescription>
            되돌리기 어려운 작업입니다. 값을 확인한 뒤 진행해 주세요.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="c-signed">계약 체결일</FieldLabel>
            <Input id="c-signed" type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="c-amount">계약 금액 (원)</FieldLabel>
            <Input id="c-amount" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="c-sido">확정 시·도</FieldLabel>
              <Input id="c-sido" value={sido} onChange={(e) => setSido(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="c-sigungu">확정 시·군·구</FieldLabel>
              <Input id="c-sigungu" value={sigungu} onChange={(e) => setSigungu(e.target.value)} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="c-open">개점 예정일</FieldLabel>
            <Input id="c-open" type="date" value={openOn} onChange={(e) => setOpenOn(e.target.value)} />
          </Field>
        </FieldGroup>

        {ready && !confirmed && (
          <Alert>
            <AlertTitle>이대로 계약 전환합니다</AlertTitle>
            <AlertDescription>
              {formatLocalDate(signedOn)} 체결, {formatKrw(Number(amount))}
              {sido ? `, ${sido} ${sigungu}`.trimEnd() : ''}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          {confirmed ? (
            <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
              계약 전환 실행
            </Button>
          ) : (
            <Button disabled={!ready} onClick={() => setConfirmed(true)}>확인</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------- 이력 탭

function HistoryTab({ leadId }: { leadId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lc-crm-lead-history', leadId],
    queryFn: () => fetchLeadHistory(leadId),
  })

  if (isLoading) return <LoadingBlock />
  if (error || !data) return <ErrorBlock message="이력을 불러오지 못했습니다." />

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>단계 이력</CardTitle>
          <CardDescription>퍼널 집계가 쓰는 업무 시각(effective_at) 기준입니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {data.stageTransitions.length === 0 && (
            <p className="text-sm text-muted-foreground">단계 변경 이력이 없습니다.</p>
          )}
          {data.stageTransitions.map((item) => (
            <div key={item.historyId} className="flex flex-col gap-1 border-b border-border pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{labelOf(LEAD_STAGE_LABEL, item.fromStage)}</span>
                <span aria-hidden>→</span>
                <StageBadge stage={item.toStage} />
              </div>
              <div className="text-xs text-muted-foreground">
                {formatKstDateTime(item.effectiveAt)}
                {item.actorUserId ? ` · 담당 #${item.actorUserId}` : ''}
                {item.reasonCode ? ` · ${item.reasonCode}` : ''}
              </div>
              {item.reasonNote && <p className="text-sm">{item.reasonNote}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>감사 로그</CardTitle>
          <CardDescription>누가 언제 무엇을 바꿨는지의 시스템 기록입니다.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {data.auditEntries.length === 0 && (
            <p className="text-sm text-muted-foreground">감사 기록이 없습니다.</p>
          )}
          {data.auditEntries.map((entry) => (
            <div key={entry.auditId} className="flex flex-col gap-1 border-b border-border pb-3 last:border-b-0 last:pb-0">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline">{labelOf(AUDIT_ACTION_LABEL, entry.action)}</Badge>
                <span className="text-xs text-muted-foreground">
                  {formatKstDateTime(entry.createdAt)}
                  {entry.actorUserId ? ` · 담당 #${entry.actorUserId}` : ''}
                </span>
              </div>
              {(entry.beforeValue || entry.afterValue) && (
                <pre className="overflow-x-auto rounded-md bg-muted p-2 text-xs text-muted-foreground">
{JSON.stringify({ before: entry.beforeValue, after: entry.afterValue }, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
